import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TablePaginationProps {
  totalCount: number;
  pageSize: number;
  currentPage: number;
  hasNextPage: boolean;
  loading: boolean;
  pageSizes: readonly number[];
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
}

/**
 * "Total · Page size · Previous 1/N Next" — the pagination footer the Broadcasts and Snapshots
 * tables share, the same shape as OptiSigns' `TablePagination`. Changing the page size returns to
 * page 1. Next is gated on the server's `hasMore`, not on arithmetic over the total.
 */
export function TablePagination({
  totalCount,
  pageSize,
  currentPage,
  hasNextPage,
  loading,
  pageSizes,
  onPageSizeChange,
  onPageChange,
}: TablePaginationProps): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground"
      data-testid="table-pagination"
    >
      <span>
        Total:{" "}
        <span className="font-medium tabular-nums text-foreground">
          {totalCount}
        </span>
      </span>
      <div className="flex items-center gap-2">
        <span>Page size:</span>
        <Select
          value={String(pageSize)}
          disabled={loading}
          onValueChange={(v) => {
            onPageSizeChange(Number(v));
            onPageChange(1);
          }}
        >
          <SelectTrigger
            className="h-8 w-[72px]"
            data-testid="pagination-page-size"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizes.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          data-testid="pagination-prev"
          disabled={currentPage <= 1 || loading}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Previous
        </Button>
        <span
          className="mx-2 min-w-[3rem] text-center tabular-nums text-foreground"
          data-testid="pagination-page"
        >
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          data-testid="pagination-next"
          disabled={!hasNextPage || loading}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
